import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage, scrollToTop } from "helpers/common";
import { toast } from "helpers/toast";
import { Banner, Button, DateInput, Input } from "components/ui";
import { moduleToFallback, moduleToSortValue, isLobModule } from "helpers/module";
import {
  daysBetween,
  defaultYearsFor,
  formatDay,
  latestIn,
  monthsBetween,
  preserveOpenPeriodLimitErrorMessage,
  suggestedRangeFor,
  timeOf,
  toDay,
  toIso,
} from "helpers/periods";
import { usePeriodStrings } from "translation/periodManagement";
import { useTranslation } from "translation/useTranslation";
import PeriodFirstSummary from "./PeriodFirstSummary";
import PeriodJoinBars from "./PeriodJoinBars";
import PeriodTakeover from "./PeriodTakeover";
import PeriodTakeoverSummary from "./PeriodTakeoverSummary";
import { defaultTakeover, takeoverConfigFor, takeoverPayload } from "./takeoverConfig";

const OPTIONAL_DATA_TO_COPY = ["employee_balanced_scorecards"];

/**
 * The form is two steps, and the second one is not optional: a takeover cannot
 * be changed after the period exists, so the summary is the only route to
 * Anlegen (LIX-1706).
 */
const STEPS = ["form", "summary"];

/** Only LOB has Leistungskarten, so only LOB offers what to do with them. */
const SCORECARD_ACTIONS = ["automatic_import", "manual_import", "automatic_import_without_objectives"];

/**
 * Creating a period is reached from the proposal bar of one lane, so the module
 * is already decided and the source can only be the period that lane ends with.
 * Neither is offered as a choice. A lane without any period has no source: that
 * creates the module's first period, with nothing to take over (LIX-1760).
 */
export default function PeriodCreateForm({ periods, module, onCancel, onCreated, setError }) {
  const { pt, ps } = usePeriodStrings();
  const { editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(false);

  const source = useMemo(() => latestIn(periods, module), [periods, module]);
  const isFirst = !source;
  const suggested = useMemo(() => suggestedRangeFor(source, module), [source, module]);

  const [name, setName] = useState(suggested.name);
  const [description, setDescription] = useState("");
  const [start, setStart] = useState(suggested.start);
  const [end, setEnd] = useState(suggested.end);
  const isDb = !isLobModule(module);
  const config = useMemo(() => takeoverConfigFor(isDb), [isDb]);

  const [takeover, setTakeover] = useState(() => defaultTakeover(config));
  const [scorecardAction, setScorecardAction] = useState("automatic_import_without_objectives");

  const [step, setStep] = useState("form");
  const stepIndex = STEPS.indexOf(step);
  const [confirmed, setConfirmed] = useState(false);
  const formRef = useRef(null);

  /** Kai asked the form to jump to the step rather than leave it to be found. */
  useEffect(() => {
    if (step === "summary") formRef.current?.scrollIntoView({ block: "start" });
  }, [step]);

  /** Going back to change something withdraws the confirmation with it. */
  const backToSelection = () => {
    setStep("form");
    setConfirmed(false);
  };

  /**
   * The module is fixed for the life of this form today, but a selection kept
   * from the other module would post area and field keys that do not exist
   * there, and takeoverPayload would quietly drop them rather than fail.
   */
  useEffect(() => setTakeover(defaultTakeover(config)), [config]);

  /** Leistungskarten are a LOB area, so the action only travels when it is on. */
  const scorecardsSelected = !isDb && takeover.areas.includes("scorecards");

  const years = defaultYearsFor(module);

  const nameEmpty = name.trim().length === 0;
  const nameTaken = periods.some((p) => p.name?.trim().toLowerCase() === name.trim().toLowerCase());

  const startsAfterSource = !source || timeOf(start) > timeOf(source.evaluation_period_end_date);
  const endsAfterStart = timeOf(end) > timeOf(start);
  const gapDays = source ? daysBetween(source.evaluation_period_end_date, start) - 1 : 0;
  const datesValid = startsAfterSource && endsAfterStart;
  const canSubmit = datesValid && !nameEmpty && !nameTaken;

  const daysLabel = (days) => ps(days === 1
    ? "period_management.days_one"
    : "period_management.days_other", { days });

  let dateHint = ps(years === 1
    ? "period_management.date.default_duration_one"
    : "period_management.date.default_duration_other", { years });
  let dateHintKind = "";

  if (!endsAfterStart) {
    dateHint = ps("period_management.date.end_before_start");
    dateHintKind = "error";
  } else if (!startsAfterSource) {
    dateHint = ps("period_management.date.start_before_source", {
      date: formatDay(source.evaluation_period_end_date),
    });
    dateHintKind = "error";
  } else if (gapDays > 0) {
    dateHint = ps("period_management.date.months", { months: monthsBetween(start, end) });
    dateHintKind = "warn";
  }

  let joinIntent = "success";
  let joinMessage = ps("period_management.join.seamless", { name: source?.name });

  if (!startsAfterSource) {
    joinIntent = "danger";
    joinMessage = ps("period_management.join.overlap", {
      date: formatDay(source.evaluation_period_end_date),
    });
  } else if (gapDays > 0) {
    joinIntent = "warning";
    joinMessage = ps("period_management.join.gap", {
      days: daysLabel(gapDays),
      module: moduleToFallback(module),
    });
  }

  const joinItems = useMemo(() => {
    const items = [];

    if (source) {
      items.push({
        type: "bar",
        variant: "source",
        kind: ps("period_management.lane.source"),
        name: source.name,
        start: source.evaluation_period_start_date,
        end: source.evaluation_period_end_date,
      });
      items.push({
        type: "slot",
        gap: startsAfterSource && gapDays > 0,
        marker: !startsAfterSource ? "error" : (gapDays > 0 ? "warn" : "ok"),
      });
    }

    items.push({
      type: "bar",
      variant: "new",
      kind: ps("period_management.lane.new"),
      name: name.trim() || suggested.name,
      start,
      end,
      invalid: !datesValid,
    });

    return items;
  }, [source, name, start, end, gapDays, startsAfterSource, datesValid, suggested.name, ps]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) return;

    /**
     * Anlegen is unreachable from the selection. Submitting there opens the
     * summary, and only the summary posts, so a period cannot come into
     * existence without its takeover having been shown once.
     */
    if (step === "form") {
      setStep("summary");
      return;
    }

    /**
     * The tick guards a takeover that cannot be undone. A first period takes
     * nothing over, so reaching the summary is all the confirmation it needs.
     */
    if (!isFirst && !confirmed) return;

    setError(null);
    setLoading(true);

    /**
     * The form sends the selection itself. copy_mode stays so the backend keeps
     * one word for what happened, but copy_areas, copy_fields and
     * employees_visible are what it has to act on: "custom" carries no scope of
     * its own. All of it comes out of takeoverPayload, which reads the same
     * derivation the summary rendered.
     */
    const identity = {
      period_name: name.trim(),
      description,
      evaluation_period_start_date: start,
      evaluation_period_end_date: end,
      module,
    };

    // The first period of a module has no source, so it sends no takeover.
    const body = isFirst ? identity : {
      ...identity,
      based_on_period: source.name,
      ...takeoverPayload(config, takeover),
      data_to_copy: scorecardsSelected ? OPTIONAL_DATA_TO_COPY : [],
      data_to_copy_employee_balanced_scorecards_action: scorecardsSelected ? scorecardAction : null,
    };

    try {
      const { status } = await api.post("/periods/new", body);

      if (status >= 200 && status < 300) {
        toast.success(ps("period_management.toast.created", { name: name.trim() }));
        onCreated();
      }
    } catch (err) {
      const formattedError = errorMessage(err);
      setError(preserveOpenPeriodLimitErrorMessage(err, formattedError));
      scrollToTop();
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The three way Zielvereinbarungen choice, unchanged, moved under the
   * Leistungskarten area it belongs to. Its labels come from the live
   * dictionary and are deliberately left alone.
   */
  /**
   * The same choice restated on the summary. The Leistungskarten description
   * says the objectives come along, and only this says what actually happens to
   * them, so the two have to be read together on the step that confirms them.
   */
  const scorecardSummary = (
    <span className="peb-summary__detail" data-testid="summary-scorecard-action">
      <strong>{pt("period_management.takeover.scorecard_action_title")}</strong>
      {editableTranslate(`new_period_data_to_copy_employee_balanced_scorecards_action.${scorecardAction}`)}
    </span>
  );

  const scorecardExtra = (
    <>
      <span className="peb-takeover__sub-title">
        {pt("period_management.takeover.scorecard_action_title")}
      </span>
      <div className="peb-checkbox-list">
        {SCORECARD_ACTIONS.map((action) => (
          <label className="peb-checkbox" key={action}>
            <input
              type="radio"
              className="peb-checkbox__input"
              name="scorecard-action"
              checked={scorecardAction === action}
              onChange={() => setScorecardAction(action)}
              data-testid={`takeover-scorecard-action-${action}`}
            />
            <span className="peb-checkbox__label">
              {editableTranslate(`new_period_data_to_copy_employee_balanced_scorecards_action.${action}`)}
            </span>
          </label>
        ))}
      </div>
    </>
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={`peb-period-form peb-mod--${moduleToSortValue(module)}`}
      data-testid="period-create-form"
    >
      <ol className="peb-steps" data-testid="period-steps">
        {STEPS.map((stepName, index) => (
          <li
            key={stepName}
            className={[
              "peb-steps__step",
              stepIndex === index ? "is-current" : "",
              stepIndex > index ? "is-done" : "",
            ].filter(Boolean).join(" ")}
            aria-current={stepIndex === index ? "step" : undefined}
            data-testid={`period-step-${stepName}`}
          >
            <span className="peb-steps__mark">
              {stepIndex > index ? <i className="bi bi-check-lg" aria-hidden="true" /> : index + 1}
            </span>
            {pt(`period_management.step.${stepName}`)}
          </li>
        ))}
      </ol>

      {step === "summary" && isFirst ? (
        <PeriodFirstSummary
          module={module}
          newName={name.trim() || suggested.name}
          start={start}
          end={end}
        />
      ) : step === "summary" ? (
        <PeriodTakeoverSummary
          config={config}
          value={takeover}
          sourceName={source?.name}
          newName={name.trim() || suggested.name}
          start={start}
          end={end}
          confirmed={confirmed}
          onConfirmedChange={setConfirmed}
          areaDetails={{ scorecards: scorecardSummary }}
        />
      ) : (
        <>
          <span className="peb-period-form__section-label">{ps("period_management.section.identification")}</span>

          <div className="peb-period-form__row">
            <div className="peb-period-form__col">
              <label className="peb-field__label" htmlFor="period-name">
                {pt("period_management.field.name")} <span className="peb-field__req">*</span>
              </label>
              <Input
                id="period-name"
                type="text"
                maxLength={255}
                error={nameEmpty || nameTaken}
                value={name}
                onChange={(e) => setName(e.target.value)} data-testid="period-name-input"
              />
              <div className={`peb-period-form__hint ${nameEmpty || nameTaken ? "peb-period-form__hint--error" : "peb-period-form__hint--ok"}`}>
                {pt(nameEmpty
                  ? "period_management.name.required"
                  : nameTaken ? "period_management.name.taken" : "period_management.name.free")}
              </div>
            </div>

            <div className="peb-period-form__col">
              <label className="peb-field__label" htmlFor="period-description">
                {pt("period_management.field.description")}{" "}
                <span className="peb-period-form__hint">{ps("period_management.field.optional")}</span>
              </label>
              <Input
                id="period-description"
                type="text"
                maxLength={50}
                placeholder={ps("period_management.field.description_placeholder", { year: toDay(start).year() })}
                value={description}
                onChange={(e) => setDescription(e.target.value)} data-testid="period-description-input"
              />
              <div className="peb-period-form__hint">{pt("period_management.field.description_hint")}</div>
            </div>

            <div className="peb-period-form__col">
              <label className="peb-field__label" htmlFor="period-start">
                {pt("period_management.detail.evaluation_period")} <span className="peb-field__req">*</span>
              </label>
              <div className="peb-period-form__dates">
                <DateInput
                  id="period-start"
                  value={start}
                  min={source ? toIso(toDay(source.evaluation_period_end_date).add(1, "day")) : null}
                  error={!startsAfterSource}
                  valueFormat="YYYY-MM-DD"
                  required
                  onChange={(event) => setStart(event.target.value)}
                  testId="period-start-input"
                />
                <span>-</span>
                <DateInput
                  id="period-end"
                  value={end}
                  min={toIso(toDay(start).add(1, "day"))}
                  error={!endsAfterStart}
                  valueFormat="YYYY-MM-DD"
                  required
                  onChange={(event) => setEnd(event.target.value)}
                  testId="period-end-input"
                />
              </div>
              <div className={`peb-period-form__hint ${dateHintKind ? `peb-period-form__hint--${dateHintKind}` : ""}`}>
                {dateHint}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <PeriodJoinBars module={module} items={joinItems} />
            {/* A first period has no predecessor to join, so a caption says so
                instead of a banner that would read as a warning. */}
            {isFirst ? (
              <div className="peb-period-form__hint mt-2" data-testid="first-period-caption">
                {ps("period_management.join.first", { module: moduleToFallback(module) })}
              </div>
            ) : (
              <Banner intent={joinIntent} className="mt-3">{joinMessage}</Banner>
            )}
          </div>

          <hr className="peb-period-form__divider" />

          {isFirst ? (
            <div data-testid="first-period-notice">
              <span className="peb-period-form__section-label">{ps("period_management.section.takeover")}</span>
              <div className="peb-period-form__hint">{pt("period_management.takeover.first_period")}</div>
            </div>
          ) : (
            <PeriodTakeover
              config={config}
              value={takeover}
              onChange={setTakeover}
              sourceName={source?.name}
              areaExtras={{ scorecards: scorecardExtra }}
            />
          )}
        </>
      )}

      <div className="peb-period-form__actions">
        {step === "summary" ? (
          <>
            <Button variant="secondary" onClick={backToSelection} data-testid="back-button">
              {ps("period_management.button.back")}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!canSubmit || (!isFirst && !confirmed)}
              loading={loading}
              data-testid="create-button"
            >
              {ps("period_management.button.create_period")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onCancel} data-testid="cancel-button">
              {ps("period_management.button.cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={!canSubmit} data-testid="continue-button">
              {ps("period_management.button.continue")}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
